import { IPcr } from '../features/pcr/store/pcr.types'
import metadata from '../metadata.json'
import { performUpgrade } from './schemaUpgrade/schemaUpgrade.util'
import Logger from '../services/logger.service'

const versionMinorIndex = 1
const versionMajorIndex = 0
const maxPointVersions = 99

const versionRegex = /^\d{1,2}\.\d{1,2}/

export const getVersion = (): string => {
  if (metadata.version) {
    return String(metadata.version)
  }

  if (process.env.REACT_APP_LOCAL_DEV_VERSION) {
    return String(process.env.REACT_APP_LOCAL_DEV_VERSION)
  }

  return 'Error: Version Not Set'
}

export const getBuildVersion = (): string => {
  return `${metadata.buildMajor}.${metadata.buildMinor}.${metadata.buildRevision} ${metadata.buildTag}`
}

export const getReleaseVersion = (versionLabel: string, buildLabel: string): string => {
  return `${versionLabel}: ${getVersion()} - ${buildLabel}: ${getBuildVersion()}`
}

export const getBuildVersionForMenu = (): string => {
  return `${metadata.buildMajor}.${metadata.buildMinor}.${metadata.buildRevision}`
}

export const getReleaseVersionForMenu = (versionLabel: string, buildLabel: string): string => {
  return `${versionLabel}: ${getVersion()} - ${buildLabel}: ${getBuildVersionForMenu()}`
}

export const validateVersions = (versions: string[]): boolean => {
  let allPass = true
  versions.forEach((v) => {
    if (!versionRegex.test(v)) {
      Logger.error(`Version '${v}' does not match pattern of ${versionRegex}`)
      allPass = false
    }
  })
  return allPass
}

export const processVersionUpgrade = (pcr: IPcr, versionTo: string): IPcr => {
  const versionFrom: string = pcr.version
  if (!validateVersions([versionFrom, versionTo])) {
    return pcr
  }
  Logger.log(`Upgrading PCR: ${versionFrom} => ${versionTo}`)

  // split the versions into major, minor, hotfix parts
  const fromVersionParts = versionFrom.split('.')
  const fromVersionPartsInt = fromVersionParts.map(Number)
  const toVersionParts = versionTo.split('.')
  const toVersionPartsInt = toVersionParts.map(Number)

  // right now we only deal with major and minor versions
  // hotfixes should not have schema upgrade work
  const majorVersionStart = fromVersionPartsInt[versionMajorIndex]
  const majorVersionEnd = toVersionPartsInt[versionMajorIndex]
  const minorVersionFirst = fromVersionPartsInt[versionMinorIndex]
  const minorVersionLast = toVersionPartsInt[versionMinorIndex]

  // loop from starting major version -> ending major version
  for (let majorVersion = majorVersionStart; majorVersion <= majorVersionEnd; majorVersion++) {
    // need to determine the minor versions to loop thru based on the current
    // major version we are upgrading to
    // For example: 14.0 -> 16.09 upgrade
    // Loop #1: major = 14, minor loops from 01 to 99 (most minor version to upgrade in that major)
    // Loop #2: major = 15, minor loops from 00 to 99 (all minor versions upgraded in that major)
    // Loop #3: major = 16, minor loops from 00 to 09 (minor versions start at 00 and end at final minor version)
    let minorVersionStart = 0
    let minorVersionEnd = maxPointVersions

    if (majorVersion === majorVersionStart) {
      // determine the starting minor version for this major loop
      // if we are in the "start" major version then we must begin with the "start" minor version + 1
      minorVersionStart = minorVersionFirst + 1 // this may end up being maxPointVersions + 1, which is OK

      // determine the ending minor version for this major loop
      if (majorVersionStart === majorVersionEnd) {
        // if we are in the "end" major version then we must stop at the "end" minor version
        minorVersionEnd = minorVersionLast
      } else {
        // otherwise we run thru all possible minor versions
        minorVersionEnd = maxPointVersions
      }
    } else if (majorVersion === majorVersionEnd) {
      // if we NOT at the "start" major version AND we are at the "end" major version
      // then our minor version always starts at 0 and stops at the last minor version
      minorVersionStart = 0
      minorVersionEnd = minorVersionLast
    }

    for (let minorVersion = minorVersionStart; minorVersion <= minorVersionEnd; minorVersion++) {
      let minorVersionStr = `${minorVersion}`
      if (minorVersion > 0 && minorVersion < 10) {
        minorVersionStr = `0${minorVersion}`
      }

      const versionForUpgrade = `${majorVersion}.${minorVersionStr}`
      performUpgrade(pcr, versionForUpgrade)
    }
  }
  return pcr
}
